// src/cockpit4/components/AuditLogEnhanced.tsx
// ============================================
// 审计与合规增强（设置中心 P0-功能2）
//
// 功能（改进计划文档 10.2 功能2）：
//   1. 操作审计日志列表（谁在什么时间做了什么）
//   2. 多维度筛选（用户/操作类型/模块/时间范围/严重程度/敏感操作）
//   3. 日志详情（点击展开，含 IP/设备/位置 + 前后数据对比）
//   4. 敏感操作标记（红色高亮）
//   5. 日志导出（CSV / JSON）
//   6. 统计图表（按日操作趋势折线图 / 模块分布饼图 / 用户排行柱状图）
//   7. 合规报告自动生成（月度 / 季度 / 年度，可导出）
//   8. 合规检查清单（内置检查项 + 异常事件检测）
//
// 增强点：
//   - 记录更多操作类型（create/update/delete/login/logout/view/export/config_change/permission_change/data_access）
//   - 记录 IP / 设备 / 位置
//   - 记录前后数据变化（beforeData / afterData）
//   - 合规报告（月度/季度/年度）
//   - 合规检查清单
//
// 数据来源（白皮书铁律一：禁止假数据）：
//   - 主数据源：localStorage(key: xeaos_audit_logs) 持久化用户真实操作
//   - 辅助数据源：RuntimeManager.query('settings', { type: 'audit_logs' })
//     拉取现有审计日志面板已有的数据并合并（运行时未注册 settings 时降级为空）
//   - 合规报告：localStorage(key: xeaos_compliance_reports) 持久化
//   无数据时显示"暂无审计日志"，绝不展示假数据
// ============================================

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  Activity,
  AlertTriangle,
  Users,
  FileDown,
  FileText,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronRight,
  Filter,
  X,
  MapPin,
  Monitor,
  Globe,
  Search,
  Sparkles,
  Calendar,
  CheckCircle2,
  XCircle,
  Eye,
  EyeOff,
  RotateCcw,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";

// ============================================
// 类型定义（与数据模型对齐）
// ============================================

/** 审计操作类型 */
type AuditActionType =
  | "create"
  | "update"
  | "delete"
  | "login"
  | "logout"
  | "view"
  | "export"
  | "config_change"
  | "permission_change"
  | "data_access";

/** 严重程度 */
type AuditSeverity = "info" | "warning" | "critical";

/** 审计日志记录 */
interface AuditLog {
  id: string;
  timestamp: string;
  userId: string;
  username: string;
  action: AuditActionType;
  /** 模块 */
  module: string;
  /** 操作对象 */
  target: string;
  /** 操作详情 */
  detail: string;
  /** IP 地址 */
  ip?: string;
  /** 设备信息 */
  device?: string;
  /** 位置信息 */
  location?: string;
  /** 变更前数据（JSON 字符串） */
  beforeData?: string;
  /** 变更后数据（JSON 字符串） */
  afterData?: string;
  /** 敏感操作标记 */
  isSensitive: boolean;
  /** 严重程度 */
  severity: AuditSeverity;
}

/** 合规报告类型 */
interface ComplianceReport {
  id: string;
  type: "monthly" | "quarterly" | "yearly";
  /** 周期标识，如 "2026-08" / "2026-Q3" / "2026" */
  period: string;
  /** 总操作数 */
  totalOperations: number;
  /** 敏感操作数 */
  sensitiveOperations: number;
  /** 活跃用户数 */
  uniqueUsers: number;
  /** 合规检查项 */
  checklist: { item: string; passed: boolean; detail: string }[];
  /** 异常事件 */
  anomalies: { type: string; count: number; description: string }[];
  /** 生成时间 */
  generatedAt: string;
}

/** 筛选条件 */
interface FilterState {
  keyword: string;
  user: string;
  action: AuditActionType | "all";
  module: string;
  severity: AuditSeverity | "all";
  sensitiveOnly: boolean;
  startDate: string;
  endDate: string;
}

// ============================================
// 常量配置
// ============================================

const STORAGE_KEY = "xeaos_audit_logs";
const REPORTS_KEY = "xeaos_compliance_reports";

/** 操作类型元信息（标签 / 颜色 / 图标） */
const ACTION_META: Record<
  AuditActionType,
  { label: string; color: string; icon: typeof Activity }
> = {
  create: { label: "创建", color: "#34D399", icon: Sparkles },
  update: { label: "更新", color: "#60A5FA", icon: Activity },
  delete: { label: "删除", color: "#F87171", icon: X },
  login: { label: "登录", color: "#34D399", icon: ShieldCheck },
  logout: { label: "登出", color: "#9CA3AF", icon: ShieldCheck },
  view: { label: "查看", color: "#A78BFA", icon: Eye },
  export: { label: "导出", color: "#FBBF24", icon: FileDown },
  config_change: { label: "配置变更", color: "#F97316", icon: Activity },
  permission_change: { label: "权限变更", color: "#EF4444", icon: ShieldAlert },
  data_access: { label: "数据访问", color: "#06B6D4", icon: Globe },
};

/** 严重程度元信息 */
const SEVERITY_META: Record<
  AuditSeverity,
  { label: string; color: string; bg: string }
> = {
  info: { label: "信息", color: "#60A5FA", bg: "rgba(96,165,250,0.10)" },
  warning: { label: "警告", color: "#FBBF24", bg: "rgba(251,191,36,0.10)" },
  critical: { label: "严重", color: "#EF4444", bg: "rgba(239,68,68,0.12)" },
};

/** 饼图模块分布配色板 */
const PIE_COLORS = [
  "#60A5FA",
  "#34D399",
  "#FBBF24",
  "#F87171",
  "#A78BFA",
  "#06B6D4",
  "#F97316",
  "#EC4899",
  "#10B981",
  "#8B5CF6",
];

/** 默认筛选状态 */
const DEFAULT_FILTER: FilterState = {
  keyword: "",
  user: "",
  action: "all",
  module: "",
  severity: "all",
  sensitiveOnly: false,
  startDate: "",
  endDate: "",
};

// ============================================
// localStorage 持久化
// ============================================

/** 从 localStorage 加载审计日志 */
function loadLogs(): AuditLog[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as AuditLog[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

/** 保存审计日志到 localStorage */
function saveLogs(logs: AuditLog[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(logs));
  } catch {
    // 静默降级
  }
}

/** 从 localStorage 加载合规报告 */
function loadReports(): ComplianceReport[] {
  try {
    const raw = localStorage.getItem(REPORTS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as ComplianceReport[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

/** 保存合规报告到 localStorage */
function saveReports(reports: ComplianceReport[]): void {
  try {
    localStorage.setItem(REPORTS_KEY, JSON.stringify(reports));
  } catch {
    // 静默降级
  }
}

// ============================================
// RuntimeManager 辅助：合并现有审计日志面板数据
// ============================================

/**
 * 从 RuntimeManager 拉取现有审计日志面板数据并归一化为 AuditLog
 *
 * 说明：RuntimeName 枚举中暂无 'settings'，这里以字符串形式调用 query。
 * 若 settings 运行时尚未注册或返回失败，则降级为空数组（不影响主数据源）。
 */
async function fetchRuntimeAuditLogs(): Promise<AuditLog[]> {
  try {
    if (!RuntimeManager.isInitialized()) return [];
    // 'settings' 运行时可能未注册，使用 try/catch 兜底
    const resp = await (RuntimeManager as any).query("settings", {
      type: "audit_logs",
    });
    if (!resp?.success) return [];
    const data = resp.data;
    // 兼容多种返回结构：直接数组 / { logs: [] } / { entries: [] }
    let rawList: any[] = [];
    if (Array.isArray(data)) {
      rawList = data;
    } else if (Array.isArray(data?.logs)) {
      rawList = data.logs;
    } else if (Array.isArray(data?.entries)) {
      rawList = data.entries;
    } else if (data?.success && Array.isArray(data?.data)) {
      rawList = data.data;
    }
    // 归一化为 AuditLog
    return rawList
      .map((item: any): AuditLog | null => {
        if (!item) return null;
        const action = (item.action as AuditActionType) || "view";
        const severity: AuditSeverity =
          item.severity === "critical" || item.severity === "warning"
            ? item.severity
            : "info";
        return {
          id: String(item.id ?? `rt_${item.timestamp ?? Date.now()}_${Math.random()}`),
          timestamp: String(item.timestamp ?? new Date().toISOString()),
          userId: String(item.userId ?? item.user_id ?? "unknown"),
          username: String(item.username ?? item.user ?? "未知用户"),
          action,
          module: String(item.module ?? "系统"),
          target: String(item.target ?? item.object ?? "-"),
          detail: String(item.detail ?? item.description ?? ""),
          ip: item.ip,
          device: item.device ?? item.userAgent,
          location: item.location,
          beforeData: item.beforeData ?? item.before,
          afterData: item.afterData ?? item.after,
          isSensitive: Boolean(item.isSensitive ?? item.sensitive ?? false),
          severity,
        };
      })
      .filter((x): x is AuditLog => x !== null);
  } catch {
    // 运行时未注册或查询异常时降级
    return [];
  }
}

// ============================================
// 工具函数
// ============================================

/** 格式化时间（YYYY-MM-DD HH:mm:ss） */
function formatTime(iso: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return iso;
  }
}

/** 格式化日期（YYYY-MM-DD） */
function formatDate(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso.slice(0, 10);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch {
    return iso.slice(0, 10);
  }
}

/** 美化 JSON 字符串（用于 beforeData / afterData 展示） */
function prettyJson(raw?: string): string {
  if (!raw) return "";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

/** 获取今日 0 点 ISO */
function todayStart(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/** 获取当前周期的标识（用于默认生成报告） */
function currentPeriod(type: ComplianceReport["type"]): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  if (type === "monthly") {
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  }
  if (type === "quarterly") {
    const q = Math.floor(now.getMonth() / 3) + 1;
    return `${now.getFullYear()}-Q${q}`;
  }
  return String(now.getFullYear());
}

/** 判断时间戳是否落在给定周期内 */
function isInPeriod(ts: string, type: ComplianceReport["type"], period: string): boolean {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return false;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (type === "monthly") {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}` === period;
  }
  if (type === "quarterly") {
    const q = Math.floor(d.getMonth() / 3) + 1;
    return `${d.getFullYear()}-Q${q}` === period;
  }
  return String(d.getFullYear()) === period;
}

/** 下载文本文件 */
function downloadText(filename: string, content: string, mime: string): void {
  try {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    // 静默降级
  }
}

/** 导出日志为 CSV */
function exportCSV(logs: AuditLog[]): void {
  const headers = [
    "ID",
    "时间",
    "用户ID",
    "用户名",
    "操作",
    "模块",
    "对象",
    "详情",
    "IP",
    "设备",
    "位置",
    "敏感",
    "严重程度",
    "变更前",
    "变更后",
  ];
  const rows = logs.map((l) => [
    l.id,
    l.timestamp,
    l.userId,
    l.username,
    ACTION_META[l.action]?.label ?? l.action,
    l.module,
    l.target,
    l.detail,
    l.ip ?? "",
    l.device ?? "",
    l.location ?? "",
    l.isSensitive ? "是" : "否",
    SEVERITY_META[l.severity]?.label ?? l.severity,
    l.beforeData ?? "",
    l.afterData ?? "",
  ]);
  // CSV 转义：含逗号/引号/换行的字段用双引号包裹，内部引号双写
  const escape = (v: string) => {
    const s = String(v ?? "");
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = [headers, ...rows].map((r) => r.map(escape).join(",")).join("\n");
  // 加 BOM 防止 Excel 中文乱码
  downloadText(`audit_logs_${Date.now()}.csv`, "\uFEFF" + csv, "text/csv;charset=utf-8;");
}

/** 导出日志为 JSON */
function exportJSON(logs: AuditLog[]): void {
  downloadText(
    `audit_logs_${Date.now()}.json`,
    JSON.stringify(logs, null, 2),
    "application/json;charset=utf-8;"
  );
}

// ============================================
// 合规检查清单（内置检查项）
// ============================================

/**
 * 基于日志数据生成合规检查清单
 * 每项返回 { item, passed, detail }
 */
function buildChecklist(logs: AuditLog[]): ComplianceReport["checklist"] {
  const total = logs.length;
  const sensitive = logs.filter((l) => l.isSensitive).length;
  const withIp = logs.filter((l) => l.ip).length;
  const withDevice = logs.filter((l) => l.device).length;
  const withDataChange = logs.filter((l) => l.beforeData || l.afterData).length;
  const critical = logs.filter((l) => l.severity === "critical").length;
  const uniqueUsers = new Set(logs.map((l) => l.userId)).size;
  const actions = new Set(logs.map((l) => l.action)).size;

  return [
    {
      item: "操作日志完整性",
      passed: total > 0,
      detail: total > 0 ? `共记录 ${total} 条操作日志` : "未记录任何操作日志",
    },
    {
      item: "敏感操作审计",
      passed: sensitive > 0,
      detail:
        sensitive > 0
          ? `共记录 ${sensitive} 条敏感操作`
          : "本周期内无敏感操作记录",
    },
    {
      item: "用户访问审计",
      passed: uniqueUsers > 0,
      detail: uniqueUsers > 0 ? `本周期活跃用户 ${uniqueUsers} 人` : "无用户活动记录",
    },
    {
      item: "异常操作检测",
      passed: critical === 0,
      detail:
        critical === 0
          ? "未发现严重级别操作"
          : `发现 ${critical} 条严重级别操作，需复查`,
    },
    {
      item: "数据变更追溯",
      passed: withDataChange > 0,
      detail:
        withDataChange > 0
          ? `${withDataChange} 条操作记录了前后数据变化`
          : "未记录数据变更前后对比",
    },
    {
      item: "IP / 设备信息记录",
      passed: withIp > 0 || withDevice > 0,
      detail: `IP 记录 ${withIp} 条，设备记录 ${withDevice} 条`,
    },
    {
      item: "操作类型多样性",
      passed: actions >= 3,
      detail: `覆盖 ${actions} 种操作类型`,
    },
  ];
}

/**
 * 异常事件检测
 * - critical 严重操作
 * - 敏感操作占比过高
 * - 数据访问异常频次
 */
function detectAnomalies(
  logs: AuditLog[]
): ComplianceReport["anomalies"] {
  const anomalies: ComplianceReport["anomalies"] = [];
  const critical = logs.filter((l) => l.severity === "critical").length;
  const sensitive = logs.filter((l) => l.isSensitive).length;
  const dataAccess = logs.filter((l) => l.action === "data_access").length;
  const deletes = logs.filter((l) => l.action === "delete").length;
  const permChanges = logs.filter((l) => l.action === "permission_change").length;

  if (critical > 0) {
    anomalies.push({
      type: "严重操作",
      count: critical,
      description: `本周期发生 ${critical} 条严重级别操作，建议逐条复核`,
    });
  }
  if (sensitive > 0) {
    anomalies.push({
      type: "敏感操作",
      count: sensitive,
      description: `本周期发生 ${sensitive} 条敏感操作，需确认是否合规`,
    });
  }
  if (dataAccess > 10) {
    anomalies.push({
      type: "数据访问频次",
      count: dataAccess,
      description: `数据访问操作 ${dataAccess} 次，频次偏高，建议核查`,
    });
  }
  if (deletes > 0) {
    anomalies.push({
      type: "删除操作",
      count: deletes,
      description: `本周期发生 ${deletes} 条删除操作，建议确认数据备份`,
    });
  }
  if (permChanges > 0) {
    anomalies.push({
      type: "权限变更",
      count: permChanges,
      description: `本周期发生 ${permChanges} 条权限变更，需审批复核`,
    });
  }
  return anomalies;
}

// ============================================
// 主组件
// ============================================

export default function AuditLogEnhanced() {
  // 日志与报告数据
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [reports, setReports] = useState<ComplianceReport[]>([]);
  const [loading, setLoading] = useState(true);

  // 筛选与交互状态
  const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [showFilterPanel, setShowFilterPanel] = useState(true);

  // 报告生成弹窗
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportType, setReportType] = useState<ComplianceReport["type"]>("monthly");
  const [reportPeriod, setReportPeriod] = useState<string>(currentPeriod("monthly"));
  const [viewingReport, setViewingReport] = useState<ComplianceReport | null>(null);

  // ============================================
  // 加载数据：合并 localStorage 主数据源 + RuntimeManager 辅助数据源
  // ============================================
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // 1. 主数据源：localStorage
      const localLogs = loadLogs();
      // 2. 辅助数据源：RuntimeManager 拉取现有审计日志面板数据
      const runtimeLogs = await fetchRuntimeAuditLogs();
      // 3. 合并去重（按 id）
      const mergedMap = new Map<string, AuditLog>();
      [...runtimeLogs, ...localLogs].forEach((l) => {
        if (!mergedMap.has(l.id)) mergedMap.set(l.id, l);
      });
      const merged = Array.from(mergedMap.values()).sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      setLogs(merged);

      // 加载合规报告
      setReports(
        loadReports().sort(
          (a, b) =>
            new Date(b.generatedAt).getTime() -
            new Date(a.generatedAt).getTime()
        )
      );
    } catch (err) {
      console.warn("[AuditLogEnhanced] 加载失败", err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // ============================================
  // 模块列表（从日志中提取，供筛选下拉使用）
  // ============================================
  const moduleOptions = useMemo(() => {
    const set = new Set<string>();
    logs.forEach((l) => set.add(l.module));
    return Array.from(set).sort();
  }, [logs]);

  // ============================================
  // 筛选后的日志
  // ============================================
  const filteredLogs = useMemo(() => {
    return logs.filter((l) => {
      // 关键词（详情/对象）
      if (filter.keyword.trim()) {
        const kw = filter.keyword.trim().toLowerCase();
        const hay = `${l.detail} ${l.target} ${l.username} ${l.module}`.toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      // 用户
      if (filter.user.trim()) {
        const u = filter.user.trim().toLowerCase();
        if (
          !l.username.toLowerCase().includes(u) &&
          !l.userId.toLowerCase().includes(u)
        )
          return false;
      }
      // 操作类型
      if (filter.action !== "all" && l.action !== filter.action) return false;
      // 模块
      if (filter.module && l.module !== filter.module) return false;
      // 严重程度
      if (filter.severity !== "all" && l.severity !== filter.severity)
        return false;
      // 仅敏感
      if (filter.sensitiveOnly && !l.isSensitive) return false;
      // 时间范围
      const ts = new Date(l.timestamp).getTime();
      if (filter.startDate) {
        const s = new Date(filter.startDate + "T00:00:00").getTime();
        if (!isNaN(s) && ts < s) return false;
      }
      if (filter.endDate) {
        const e = new Date(filter.endDate + "T23:59:59").getTime();
        if (!isNaN(e) && ts > e) return false;
      }
      return true;
    });
  }, [logs, filter]);

  // ============================================
  // 统计卡片数据
  // ============================================
  const stats = useMemo(() => {
    const todayStartTs = new Date(todayStart()).getTime();
    const todayOps = logs.filter(
      (l) => new Date(l.timestamp).getTime() >= todayStartTs
    ).length;
    const sensitiveOps = logs.filter((l) => l.isSensitive).length;
    const anomalies = logs.filter(
      (l) => l.severity === "critical" || l.severity === "warning"
    ).length;
    const uniqueUsers = new Set(logs.map((l) => l.userId)).size;
    return { todayOps, sensitiveOps, anomalies, uniqueUsers };
  }, [logs]);

  // ============================================
  // 图表数据
  // ============================================

  // 操作趋势：最近 14 天每天的 操作数
  const trendData = useMemo(() => {
    const days = 14;
    const map = new Map<string, number>();
    const today = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = formatDate(d.toISOString());
      map.set(key, 0);
    }
    logs.forEach((l) => {
      const key = formatDate(l.timestamp);
      if (map.has(key)) map.set(key, (map.get(key) ?? 0) + 1);
    });
    return Array.from(map.entries()).map(([date, count]) => ({
      date: date.slice(5), // MM-DD
      count,
    }));
  }, [logs]);

  // 模块分布饼图
  const moduleDist = useMemo(() => {
    const map = new Map<string, number>();
    logs.forEach((l) => {
      map.set(l.module, (map.get(l.module) ?? 0) + 1);
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [logs]);

  // 用户操作排行（Top 8）
  const userRanking = useMemo(() => {
    const map = new Map<string, number>();
    logs.forEach((l) => {
      map.set(l.username, (map.get(l.username) ?? 0) + 1);
    });
    return Array.from(map.entries())
      .map(([user, count]) => ({ user, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [logs]);

  // ============================================
  // 重置筛选
  // ============================================
  const resetFilter = useCallback(() => {
    setFilter(DEFAULT_FILTER);
  }, []);

  // ============================================
  // 生成合规报告
  // ============================================
  const generateReport = useCallback(() => {
    const periodLogs = logs.filter((l) =>
      isInPeriod(l.timestamp, reportType, reportPeriod)
    );
    const checklist = buildChecklist(periodLogs);
    const anomalies = detectAnomalies(periodLogs);
    const report: ComplianceReport = {
      id: `report_${reportType}_${reportPeriod}_${Date.now()}`,
      type: reportType,
      period: reportPeriod,
      totalOperations: periodLogs.length,
      sensitiveOperations: periodLogs.filter((l) => l.isSensitive).length,
      uniqueUsers: new Set(periodLogs.map((l) => l.userId)).size,
      checklist,
      anomalies,
      generatedAt: new Date().toISOString(),
    };
    const next = [report, ...reports];
    setReports(next);
    saveReports(next);
    setReportModalOpen(false);
    setViewingReport(report);
  }, [logs, reportType, reportPeriod, reports]);

  // ============================================
  // 删除报告
  // ============================================
  const deleteReport = useCallback(
    (id: string) => {
      const next = reports.filter((r) => r.id !== id);
      setReports(next);
      saveReports(next);
      if (viewingReport?.id === id) setViewingReport(null);
    },
    [reports, viewingReport]
  );

  // ============================================
  // 渲染辅助
  // ============================================

  /** 渲染顶部统计卡片 */
  const renderStatCard = (
    icon: typeof Activity,
    label: string,
    value: number | string,
    color: string,
    sub?: string
  ) => {
    const Icon = icon;
    return (
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-4 flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: `${color}1a`, border: `1px solid ${color}33` }}
        >
          <Icon size={18} style={{ color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-mono text-gray-500 truncate">
            {label}
          </div>
          <div
            className="text-[22px] font-bold font-mono leading-tight"
            style={{ color }}
          >
            {value}
          </div>
          {sub && (
            <div className="text-[10px] font-mono text-gray-600 truncate">
              {sub}
            </div>
          )}
        </div>
      </div>
    );
  };

  /** 渲染操作类型标签 */
  const renderActionBadge = (action: AuditActionType) => {
    const meta = ACTION_META[action] ?? ACTION_META.view;
    const Icon = meta.icon;
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono"
        style={{ color: meta.color, background: `${meta.color}1a` }}
      >
        <Icon size={10} />
        {meta.label}
      </span>
    );
  };

  /** 渲染严重程度标签 */
  const renderSeverityBadge = (severity: AuditSeverity) => {
    const meta = SEVERITY_META[severity];
    return (
      <span
        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono"
        style={{ color: meta.color, background: meta.bg }}
      >
        {meta.label}
      </span>
    );
  };

  /** 渲染日志详情（前后数据对比） */
  const renderDataDiff = (before?: string, after?: string) => {
    if (!before && !after) {
      return (
        <div className="text-[11px] font-mono text-gray-600 italic">
          无数据变更记录
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-md border border-red-500/20 bg-red-500/[0.04] p-2">
          <div className="text-[10px] font-mono text-red-400 mb-1 flex items-center gap-1">
            <XCircle size={10} /> 变更前
          </div>
          <pre className="text-[10px] font-mono text-gray-400 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
            {prettyJson(before) || "（空）"}
          </pre>
        </div>
        <div className="rounded-md border border-emerald-500/20 bg-emerald-500/[0.04] p-2">
          <div className="text-[10px] font-mono text-emerald-400 mb-1 flex items-center gap-1">
            <CheckCircle2 size={10} /> 变更后
          </div>
          <pre className="text-[10px] font-mono text-gray-400 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
            {prettyJson(after) || "（空）"}
          </pre>
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
    >
      {/* ============================================ */}
      {/* 头部 */}
      {/* ============================================ */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-emerald-400" />
          <span className="text-[14px] font-medium text-white">
            审计与合规增强
          </span>
          <span className="text-[10px] font-mono text-gray-600">
            数据源：localStorage + RuntimeManager
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportCSV(filteredLogs)}
            disabled={filteredLogs.length === 0}
            title="导出当前筛选结果为 CSV"
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors disabled:opacity-40"
          >
            <FileDown size={11} className="text-emerald-400" />
            <span className="text-[11px] font-mono text-emerald-300">CSV</span>
          </button>
          <button
            onClick={() => exportJSON(filteredLogs)}
            disabled={filteredLogs.length === 0}
            title="导出当前筛选结果为 JSON"
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20 transition-colors disabled:opacity-40"
          >
            <FileDown size={11} className="text-sky-400" />
            <span className="text-[11px] font-mono text-sky-300">JSON</span>
          </button>
          <button
            onClick={refresh}
            disabled={loading}
            title="刷新审计日志"
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.06] transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="animate-spin text-gray-300" />
            ) : (
              <RefreshCw size={11} className="text-gray-300" />
            )}
            <span className="text-[11px] font-mono text-gray-300">刷新</span>
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* ============================================ */}
        {/* 顶部：统计卡片 + 生成报告按钮 */}
        {/* ============================================ */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {renderStatCard(
            Activity,
            "今日操作数",
            stats.todayOps,
            "#60A5FA",
            `共 ${logs.length} 条`
          )}
          {renderStatCard(
            ShieldAlert,
            "敏感操作",
            stats.sensitiveOps,
            "#EF4444"
          )}
          {renderStatCard(
            AlertTriangle,
            "异常事件",
            stats.anomalies,
            "#FBBF24",
            "警告 + 严重"
          )}
          {renderStatCard(Users, "活跃用户", stats.uniqueUsers, "#34D399")}
          {/* 生成报告按钮卡片 */}
          <button
            onClick={() => {
              setReportType("monthly");
              setReportPeriod(currentPeriod("monthly"));
              setReportModalOpen(true);
            }}
            className="rounded-xl border border-violet-500/30 bg-violet-500/[0.06] hover:bg-violet-500/[0.12] transition-colors p-4 flex items-center gap-3 text-left"
          >
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              style={{
                background: "rgba(167,139,250,0.10)",
                border: "1px solid rgba(167,139,250,0.25)",
              }}
            >
              <FileText size={18} className="text-violet-400" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-mono text-gray-500">
                合规报告
              </div>
              <div className="text-[14px] font-bold text-violet-300">
                生成报告
              </div>
              <div className="text-[10px] font-mono text-gray-600">
                月度 / 季度 / 年度
              </div>
            </div>
          </button>
        </div>

        {/* ============================================ */}
        {/* 中部：左侧日志列表 + 右侧图表 */}
        {/* ============================================ */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* ---------- 左侧：筛选栏 + 日志列表 ---------- */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden flex flex-col">
            {/* 筛选栏头部 */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
              <button
                onClick={() => setShowFilterPanel((v) => !v)}
                className="flex items-center gap-1.5 text-[12px] font-medium text-gray-200 hover:text-white"
              >
                <Filter size={12} className="text-sky-400" />
                筛选
                {showFilterPanel ? (
                  <ChevronDown size={12} />
                ) : (
                  <ChevronRight size={12} />
                )}
                <span className="text-[10px] font-mono text-gray-500">
                  ({filteredLogs.length}/{logs.length})
                </span>
              </button>
              <button
                onClick={resetFilter}
                className="flex items-center gap-1 text-[10px] font-mono text-gray-500 hover:text-gray-300"
              >
                <RotateCcw size={10} /> 重置
              </button>
            </div>

            {/* 筛选面板 */}
            <AnimatePresence>
              {showFilterPanel && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden border-b border-white/[0.06]"
                >
                  <div className="p-3 grid grid-cols-2 gap-2">
                    {/* 关键词 */}
                    <div className="col-span-2">
                      <label className="text-[10px] font-mono text-gray-500">
                        关键词（详情/对象/用户/模块）
                      </label>
                      <div className="relative mt-1">
                        <Search
                          size={11}
                          className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-600"
                        />
                        <input
                          value={filter.keyword}
                          onChange={(e) =>
                            setFilter((f) => ({ ...f, keyword: e.target.value }))
                          }
                          placeholder="输入关键词搜索..."
                          className="w-full pl-7 pr-2 py-1 text-[11px] font-mono bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-sky-500/40"
                        />
                      </div>
                    </div>
                    {/* 用户 */}
                    <div>
                      <label className="text-[10px] font-mono text-gray-500">
                        用户
                      </label>
                      <input
                        value={filter.user}
                        onChange={(e) =>
                          setFilter((f) => ({ ...f, user: e.target.value }))
                        }
                        placeholder="用户名/ID"
                        className="mt-1 w-full px-2 py-1 text-[11px] font-mono bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-sky-500/40"
                      />
                    </div>
                    {/* 操作类型 */}
                    <div>
                      <label className="text-[10px] font-mono text-gray-500">
                        操作类型
                      </label>
                      <select
                        value={filter.action}
                        onChange={(e) =>
                          setFilter((f) => ({
                            ...f,
                            action: e.target.value as FilterState["action"],
                          }))
                        }
                        className="mt-1 w-full px-2 py-1 text-[11px] font-mono bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 focus:outline-none focus:border-sky-500/40"
                      >
                        <option value="all">全部操作</option>
                        {(Object.keys(ACTION_META) as AuditActionType[]).map(
                          (a) => (
                            <option key={a} value={a}>
                              {ACTION_META[a].label}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    {/* 模块 */}
                    <div>
                      <label className="text-[10px] font-mono text-gray-500">
                        模块
                      </label>
                      <select
                        value={filter.module}
                        onChange={(e) =>
                          setFilter((f) => ({ ...f, module: e.target.value }))
                        }
                        className="mt-1 w-full px-2 py-1 text-[11px] font-mono bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 focus:outline-none focus:border-sky-500/40"
                      >
                        <option value="">全部模块</option>
                        {moduleOptions.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* 严重程度 */}
                    <div>
                      <label className="text-[10px] font-mono text-gray-500">
                        严重程度
                      </label>
                      <select
                        value={filter.severity}
                        onChange={(e) =>
                          setFilter((f) => ({
                            ...f,
                            severity: e.target.value as FilterState["severity"],
                          }))
                        }
                        className="mt-1 w-full px-2 py-1 text-[11px] font-mono bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 focus:outline-none focus:border-sky-500/40"
                      >
                        <option value="all">全部程度</option>
                        <option value="info">信息</option>
                        <option value="warning">警告</option>
                        <option value="critical">严重</option>
                      </select>
                    </div>
                    {/* 时间范围 */}
                    <div>
                      <label className="text-[10px] font-mono text-gray-500">
                        起始日期
                      </label>
                      <input
                        type="date"
                        value={filter.startDate}
                        onChange={(e) =>
                          setFilter((f) => ({ ...f, startDate: e.target.value }))
                        }
                        className="mt-1 w-full px-2 py-1 text-[11px] font-mono bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 focus:outline-none focus:border-sky-500/40"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-mono text-gray-500">
                        结束日期
                      </label>
                      <input
                        type="date"
                        value={filter.endDate}
                        onChange={(e) =>
                          setFilter((f) => ({ ...f, endDate: e.target.value }))
                        }
                        className="mt-1 w-full px-2 py-1 text-[11px] font-mono bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 focus:outline-none focus:border-sky-500/40"
                      />
                    </div>
                    {/* 仅敏感 */}
                    <label className="col-span-2 flex items-center gap-2 text-[11px] font-mono text-gray-400 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={filter.sensitiveOnly}
                        onChange={(e) =>
                          setFilter((f) => ({
                            ...f,
                            sensitiveOnly: e.target.checked,
                          }))
                        }
                        className="accent-red-500"
                      />
                      <ShieldAlert size={11} className="text-red-400" />
                      仅显示敏感操作
                    </label>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* 日志列表 */}
            <div className="max-h-[560px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-10 text-[12px] text-gray-500">
                  <Loader2 size={14} className="animate-spin mr-2" /> 加载审计日志...
                </div>
              ) : filteredLogs.length === 0 ? (
                <div className="text-[12px] text-gray-600 italic py-10 text-center">
                  暂无审计日志
                  <div className="text-[10px] text-gray-700 mt-2">
                    用户操作会自动记录到 localStorage(key: xeaos_audit_logs)
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-white/[0.03]">
                  {filteredLogs.map((log) => {
                    const expanded = expandedLogId === log.id;
                    const isSensitive = log.isSensitive;
                    return (
                      <div
                        key={log.id}
                        className={`transition-colors ${
                          isSensitive
                            ? "bg-red-500/[0.04] hover:bg-red-500/[0.07]"
                            : "hover:bg-white/[0.02]"
                        }`}
                      >
                        {/* 日志摘要行 */}
                        <button
                          onClick={() =>
                            setExpandedLogId(expanded ? null : log.id)
                          }
                          className="w-full flex items-center gap-2 px-3 py-2 text-left"
                        >
                          {expanded ? (
                            <ChevronDown
                              size={11}
                              className="text-gray-500 shrink-0"
                            />
                          ) : (
                            <ChevronRight
                              size={11}
                              className="text-gray-500 shrink-0"
                            />
                          )}
                          <span className="text-[10px] font-mono text-gray-500 w-32 shrink-0 truncate">
                            {formatTime(log.timestamp)}
                          </span>
                          <span className="text-[11px] font-mono text-gray-200 w-24 shrink-0 truncate">
                            {log.username}
                          </span>
                          <div className="shrink-0">
                            {renderActionBadge(log.action)}
                          </div>
                          <span className="text-[10px] font-mono text-gray-500 w-20 shrink-0 truncate">
                            {log.module}
                          </span>
                          <span className="text-[11px] font-mono text-gray-300 flex-1 truncate">
                            {log.target}
                          </span>
                          <div className="shrink-0">
                            {renderSeverityBadge(log.severity)}
                          </div>
                          {isSensitive && (
                            <ShieldAlert
                              size={11}
                              className="text-red-400 shrink-0"
                            />
                          )}
                        </button>
                        {/* 日志详情 */}
                        <AnimatePresence>
                          {expanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="px-3 pb-3 pt-1 space-y-3">
                                {/* 详情描述 */}
                                <div className="rounded-md border border-white/[0.06] bg-white/[0.01] p-2">
                                  <div className="text-[10px] font-mono text-gray-500 mb-1">
                                    操作详情
                                  </div>
                                  <div className="text-[11px] font-mono text-gray-300 break-all">
                                    {log.detail || "（无详情）"}
                                  </div>
                                </div>
                                {/* IP / 设备 / 位置 */}
                                <div className="grid grid-cols-3 gap-2">
                                  <div className="rounded-md border border-white/[0.06] bg-white/[0.01] p-2">
                                    <div className="text-[10px] font-mono text-gray-500 mb-1 flex items-center gap-1">
                                      <Globe size={10} /> IP
                                    </div>
                                    <div className="text-[11px] font-mono text-gray-300 break-all">
                                      {log.ip || "—"}
                                    </div>
                                  </div>
                                  <div className="rounded-md border border-white/[0.06] bg-white/[0.01] p-2">
                                    <div className="text-[10px] font-mono text-gray-500 mb-1 flex items-center gap-1">
                                      <Monitor size={10} /> 设备
                                    </div>
                                    <div className="text-[11px] font-mono text-gray-300 break-all">
                                      {log.device || "—"}
                                    </div>
                                  </div>
                                  <div className="rounded-md border border-white/[0.06] bg-white/[0.01] p-2">
                                    <div className="text-[10px] font-mono text-gray-500 mb-1 flex items-center gap-1">
                                      <MapPin size={10} /> 位置
                                    </div>
                                    <div className="text-[11px] font-mono text-gray-300 break-all">
                                      {log.location || "—"}
                                    </div>
                                  </div>
                                </div>
                                {/* 前后数据对比 */}
                                <div>
                                  <div className="text-[10px] font-mono text-gray-500 mb-1">
                                    数据变更对比
                                  </div>
                                  {renderDataDiff(log.beforeData, log.afterData)}
                                </div>
                                {/* 元信息 */}
                                <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono text-gray-600">
                                  <span>ID: {log.id}</span>
                                  <span>用户ID: {log.userId}</span>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ---------- 右侧：统计图表 ---------- */}
          <div className="space-y-4">
            {/* 操作趋势折线图 */}
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Activity size={12} className="text-sky-400" />
                  <span className="text-[12px] font-medium text-gray-200">
                    操作趋势（近 14 天）
                  </span>
                </div>
              </div>
              <div className="h-[180px]">
                {trendData.every((d) => d.count === 0) ? (
                  <EmptyChart text="暂无操作数据" />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData}>
                      <defs>
                        <linearGradient id="auditTrend" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.5} />
                          <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#ffffff10" strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                        axisLine={{ stroke: "#ffffff20" }}
                      />
                      <YAxis
                        tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                        axisLine={{ stroke: "#ffffff20" }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "rgba(20,20,30,0.95)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          borderRadius: 8,
                          fontSize: 11,
                          fontFamily: "monospace",
                        }}
                        labelStyle={{ color: "#9CA3AF" }}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        name="操作数"
                        stroke="#60A5FA"
                        strokeWidth={2}
                        fill="url(#auditTrend)"
                        dot={{ r: 2, fill: "#60A5FA" }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* 模块分布饼图 + 用户排行柱状图 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Globe size={12} className="text-violet-400" />
                  <span className="text-[12px] font-medium text-gray-200">
                    模块分布
                  </span>
                </div>
                <div className="h-[180px]">
                  {moduleDist.length === 0 ? (
                    <EmptyChart text="暂无模块数据" />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={moduleDist}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={60}
                          innerRadius={30}
                          paddingAngle={2}
                        >
                          {moduleDist.map((_, idx) => (
                            <Cell
                              key={idx}
                              fill={PIE_COLORS[idx % PIE_COLORS.length]}
                              stroke="rgba(0,0,0,0.3)"
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            background: "rgba(20,20,30,0.95)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: 8,
                            fontSize: 11,
                            fontFamily: "monospace",
                          }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: 10, fontFamily: "monospace" }}
                          iconSize={8}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Users size={12} className="text-emerald-400" />
                  <span className="text-[12px] font-medium text-gray-200">
                    用户操作排行（Top 8）
                  </span>
                </div>
                <div className="h-[180px]">
                  {userRanking.length === 0 ? (
                    <EmptyChart text="暂无用户数据" />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={userRanking}
                        layout="vertical"
                        margin={{ left: 10, right: 10, top: 0, bottom: 0 }}
                      >
                        <CartesianGrid stroke="#ffffff10" strokeDasharray="3 3" />
                        <XAxis
                          type="number"
                          tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                          axisLine={{ stroke: "#ffffff20" }}
                          allowDecimals={false}
                        />
                        <YAxis
                          type="category"
                          dataKey="user"
                          tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                          axisLine={{ stroke: "#ffffff20" }}
                          width={70}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "rgba(20,20,30,0.95)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: 8,
                            fontSize: 11,
                            fontFamily: "monospace",
                          }}
                          cursor={{ fill: "rgba(255,255,255,0.04)" }}
                        />
                        <Bar
                          dataKey="count"
                          name="操作数"
                          fill="#34D399"
                          radius={[0, 3, 3, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================ */}
        {/* 底部：合规报告列表 */}
        {/* ============================================ */}
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
            <div className="flex items-center gap-1.5">
              <FileText size={12} className="text-violet-400" />
              <span className="text-[12px] font-medium text-gray-200">
                合规报告
              </span>
              <span className="text-[10px] font-mono text-gray-600">
                ({reports.length})
              </span>
            </div>
            <button
              onClick={() => {
                setReportType("monthly");
                setReportPeriod(currentPeriod("monthly"));
                setReportModalOpen(true);
              }}
              className="flex items-center gap-1 px-2 py-1 rounded-md border border-violet-500/30 bg-violet-500/10 hover:bg-violet-500/20 transition-colors"
            >
              <Sparkles size={11} className="text-violet-400" />
              <span className="text-[11px] font-mono text-violet-300">
                生成报告
              </span>
            </button>
          </div>

          {reports.length === 0 ? (
            <div className="text-[12px] text-gray-600 italic py-8 text-center">
              暂无合规报告，点击"生成报告"创建
            </div>
          ) : (
            <div className="divide-y divide-white/[0.03] max-h-[320px] overflow-y-auto">
              {reports.map((r) => {
                const passedCount = r.checklist.filter((c) => c.passed).length;
                const allPassed = passedCount === r.checklist.length;
                return (
                  <div
                    key={r.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-white/[0.02]"
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{
                        background: allPassed
                          ? "rgba(52,211,153,0.10)"
                          : "rgba(251,191,36,0.10)",
                        border: `1px solid ${
                          allPassed
                            ? "rgba(52,211,153,0.25)"
                            : "rgba(251,191,36,0.25)"
                        }`,
                      }}
                    >
                      {allPassed ? (
                        <ShieldCheck size={14} className="text-emerald-400" />
                      ) : (
                        <AlertTriangle size={14} className="text-amber-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-mono text-gray-200">
                          {r.period}
                        </span>
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">
                          {r.type === "monthly"
                            ? "月度"
                            : r.type === "quarterly"
                            ? "季度"
                            : "年度"}
                        </span>
                      </div>
                      <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                        操作 {r.totalOperations} · 敏感 {r.sensitiveOperations} ·
                        用户 {r.uniqueUsers} · 检查 {passedCount}/{r.checklist.length}
                        · 异常 {r.anomalies.length}
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-gray-600 shrink-0">
                      {formatTime(r.generatedAt)}
                    </span>
                    <button
                      onClick={() => setViewingReport(r)}
                      className="px-2 py-1 rounded border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.06] text-[10px] font-mono text-gray-300 shrink-0"
                    >
                      查看
                    </button>
                    <button
                      onClick={() => deleteReport(r.id)}
                      className="p-1 rounded border border-red-500/20 bg-red-500/[0.04] hover:bg-red-500/[0.10] text-red-400 shrink-0"
                      title="删除报告"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* 生成报告弹窗 */}
      {/* ============================================ */}
      <AnimatePresence>
        {reportModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setReportModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-xl border border-white/[0.08] bg-[#0d0d12] p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-violet-400" />
                  <span className="text-[14px] font-medium text-white">
                    生成合规报告
                  </span>
                </div>
                <button
                  onClick={() => setReportModalOpen(false)}
                  className="text-gray-500 hover:text-gray-300"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-[11px] font-mono text-gray-500">
                    报告类型
                  </label>
                  <div className="mt-1 grid grid-cols-3 gap-2">
                    {(["monthly", "quarterly", "yearly"] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => {
                          setReportType(t);
                          setReportPeriod(currentPeriod(t));
                        }}
                        className={`px-2 py-1.5 rounded-md text-[11px] font-mono border transition-colors ${
                          reportType === t
                            ? "border-violet-500/40 bg-violet-500/15 text-violet-300"
                            : "border-white/[0.06] bg-white/[0.02] text-gray-400 hover:bg-white/[0.05]"
                        }`}
                      >
                        {t === "monthly"
                          ? "月度"
                          : t === "quarterly"
                          ? "季度"
                          : "年度"}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-mono text-gray-500">
                    周期标识
                  </label>
                  <input
                    value={reportPeriod}
                    onChange={(e) => setReportPeriod(e.target.value)}
                    placeholder={
                      reportType === "monthly"
                        ? "如 2026-08"
                        : reportType === "quarterly"
                        ? "如 2026-Q3"
                        : "如 2026"
                    }
                    className="mt-1 w-full px-2 py-1.5 text-[11px] font-mono bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-violet-500/40"
                  />
                </div>
                <div className="rounded-md border border-white/[0.06] bg-white/[0.01] p-2 text-[10px] font-mono text-gray-500">
                  将基于 {reportPeriod} 周期内的 {logs.length} 条日志生成报告，
                  包含合规检查清单与异常事件检测。
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    onClick={() => setReportModalOpen(false)}
                    className="px-3 py-1.5 rounded-md text-[11px] font-mono text-gray-400 hover:text-gray-200 border border-white/[0.06] bg-white/[0.02]"
                  >
                    取消
                  </button>
                  <button
                    onClick={generateReport}
                    className="px-3 py-1.5 rounded-md text-[11px] font-mono text-violet-200 border border-violet-500/30 bg-violet-500/15 hover:bg-violet-500/25"
                  >
                    生成
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============================================ */}
      {/* 报告详情弹窗 */}
      {/* ============================================ */}
      <AnimatePresence>
        {viewingReport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setViewingReport(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-xl border border-white/[0.08] bg-[#0d0d12] p-4"
            >
              <div className="flex items-center justify-between mb-3 sticky top-0 bg-[#0d0d12] pb-2 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-violet-400" />
                  <span className="text-[14px] font-medium text-white">
                    合规报告 · {viewingReport.period}
                  </span>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">
                    {viewingReport.type === "monthly"
                      ? "月度"
                      : viewingReport.type === "quarterly"
                      ? "季度"
                      : "年度"}
                  </span>
                </div>
                <button
                  onClick={() => setViewingReport(null)}
                  className="text-gray-500 hover:text-gray-300"
                >
                  <X size={14} />
                </button>
              </div>

              {/* 概要统计 */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <ReportStat
                  label="总操作数"
                  value={viewingReport.totalOperations}
                  color="#60A5FA"
                />
                <ReportStat
                  label="敏感操作"
                  value={viewingReport.sensitiveOperations}
                  color="#EF4444"
                />
                <ReportStat
                  label="活跃用户"
                  value={viewingReport.uniqueUsers}
                  color="#34D399"
                />
              </div>

              {/* 合规检查清单 */}
              <div className="mb-3">
                <div className="text-[12px] font-medium text-gray-200 mb-2 flex items-center gap-1.5">
                  <ShieldCheck size={12} className="text-emerald-400" />
                  合规检查清单
                </div>
                <div className="space-y-1.5">
                  {viewingReport.checklist.map((c, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 rounded-md border border-white/[0.06] bg-white/[0.01] p-2"
                    >
                      {c.passed ? (
                        <CheckCircle2
                          size={12}
                          className="text-emerald-400 mt-0.5 shrink-0"
                        />
                      ) : (
                        <XCircle
                          size={12}
                          className="text-red-400 mt-0.5 shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <div className="text-[11px] font-mono text-gray-200">
                          {c.item}
                        </div>
                        <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                          {c.detail}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 异常事件 */}
              <div className="mb-3">
                <div className="text-[12px] font-medium text-gray-200 mb-2 flex items-center gap-1.5">
                  <AlertTriangle size={12} className="text-amber-400" />
                  异常事件
                </div>
                {viewingReport.anomalies.length === 0 ? (
                  <div className="text-[11px] font-mono text-gray-600 italic py-2 text-center rounded-md border border-white/[0.06] bg-white/[0.01]">
                    未检测到异常事件
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {viewingReport.anomalies.map((a, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 rounded-md border border-amber-500/15 bg-amber-500/[0.04] p-2"
                      >
                        <AlertTriangle
                          size={12}
                          className="text-amber-400 mt-0.5 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-mono text-amber-300">
                            {a.type}（{a.count} 次）
                          </div>
                          <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                            {a.description}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 底部：导出 + 关闭 */}
              <div className="flex justify-between items-center pt-2 border-t border-white/[0.06]">
                <span className="text-[10px] font-mono text-gray-600">
                  生成于 {formatTime(viewingReport.generatedAt)}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      downloadText(
                        `compliance_report_${viewingReport.period}.json`,
                        JSON.stringify(viewingReport, null, 2),
                        "application/json;charset=utf-8;"
                      )
                    }
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-mono text-sky-300 border border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20"
                  >
                    <FileDown size={11} /> 导出 JSON
                  </button>
                  <button
                    onClick={() => setViewingReport(null)}
                    className="px-3 py-1.5 rounded-md text-[11px] font-mono text-gray-300 border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.06]"
                  >
                    关闭
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：图表空状态
// ============================================

function EmptyChart({ text }: { text: string }): ReactNode {
  return (
    <div className="h-full flex flex-col items-center justify-center text-gray-600">
      <EyeOff size={20} className="mb-1 opacity-50" />
      <span className="text-[11px] font-mono">{text}</span>
    </div>
  );
}

// ============================================
// 子组件：报告统计小卡片
// ============================================

function ReportStat({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}): ReactNode {
  return (
    <div
      className="rounded-md border p-2 text-center"
      style={{
        borderColor: `${color}33`,
        background: `${color}0d`,
      }}
    >
      <div className="text-[10px] font-mono text-gray-500">{label}</div>
      <div
        className="text-[18px] font-bold font-mono mt-0.5"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

/** 内部使用：Trash2 图标（避免顶部 lucide 导入遗漏） */
import { Trash2 } from "lucide-react";
